import { authConfig } from "@/auth/auth.config";
import { getAccessToken, refreshSession } from "@/auth/session";

/** Error from the platform API with its HTTP status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;

/**
 * fetch() for the platform API: adds the access token, and on a 401 renews
 * the session once and retries (e.g. the token expired while the tab slept).
 */
export async function apiFetch(path: string, init: FetchInit = {}): Promise<Response> {
  const url = `${authConfig().apiBaseUrl}${path}`;
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(url, { ...init, headers });
  };

  const token = await getAccessToken();
  const response = await send(token);
  if (response.status !== 401 || !token) return response;
  return (await refreshSession()) ? send(await getAccessToken()) : response;
}

/** GET a JSON resource; throws ApiError for non-2xx answers. */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(path, { signal });
  if (!response.ok) throw new ApiError(response.status, `GET ${path} failed (${response.status})`);
  return (await response.json()) as T;
}

/**
 * POST/PUT/PATCH/DELETE with an optional JSON body. Throws ApiError carrying
 * the API's own message (e.g. "This email already has an account."), so it
 * can be shown to the user as is. Resolves to undefined for 204 answers.
 */
export async function apiSend<T = void>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await apiFetch(path, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new ApiError(response.status, await errorMessage(response));
  return (response.status === 204 ? undefined : await response.json()) as T;
}

/** NestJS errors look like { statusCode, message: string | string[], error }. */
async function errorMessage(response: Response): Promise<string> {
  try {
    const { message } = (await response.json()) as { message?: unknown };
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string" && message) return message;
  } catch {
    // Not JSON (e.g. a gateway error page).
  }
  return `Request failed (${response.status})`;
}
